import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { formatCurrency } from '../../../utils/currency';

const InvoiceStatusCard = ({ invoiceBreakdown = [], currency = null }) => {
  const formatAmount = (amount) => {
    if (!currency) return formatCurrency(amount || 0);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0);
    } catch (error) {
      return formatCurrency(amount || 0);
    }
  };

  const COLORS = {
    'Draft': '#94a3b8',
    'Sent': '#3b82f6',
    'Paid': '#10b981',
    'Overdue': '#ef4444',
    'Cancelled': '#6b7280'
  };

  const chartData = invoiceBreakdown.map(item => ({
    name: item.status,
    value: parseFloat(item.total_amount || 0),
    count: parseInt(item.invoice_count || 0),
    color: COLORS[item.status] || '#6b7280'
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatAmount(data.value)}
          </p>
          <p className="text-xs text-muted-foreground">
            {data.payload.count} invoice{data.payload.count !== 1 ? 's' : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
      <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4">
        Invoice Status
      </h2>

      {chartData.length > 0 ? (
        <>
          <div className="w-full h-48 md:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 space-y-2">
            {invoiceBreakdown.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-2 rounded hover:bg-muted transition-smooth">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[item.status] || '#6b7280' }}
                  />
                  <span className="text-sm text-foreground">{item.status}</span>
                  <span className="text-xs text-muted-foreground">({item.invoice_count})</span>
                </div>
                <span className="text-sm font-medium text-foreground">
                  {formatAmount(item.total_amount)}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No invoice data available</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceStatusCard;
